import { format } from 'date-fns';
import { formatCurrency } from '@/lib/utils';
import type { TemplateId } from './InvoiceTemplates';
import type { Quotation } from '@/types/invoice';
import { SafeLogoImage } from './SafeLogoImage';

interface QuotationPrintViewProps {
  quotation: Quotation;
  elementId?: string;
  noShadow?: boolean;
}

interface ColorTokens {
  accent: string;
  border: string;
  bg: string;
  textOnAccent: string;
  badgeBg: string;
  badgeText: string;
}

const COLOR_THEMES: Record<string, ColorTokens> = {
  slate: {
    accent: '#475569',
    border: '#cbd5e1',
    bg: '#f8fafc',
    textOnAccent: '#ffffff',
    badgeBg: '#f1f5f9',
    badgeText: '#334155',
  },
  blue: {
    accent: '#1e40af',
    border: '#dbeafe',
    bg: '#eff6ff',
    textOnAccent: '#ffffff',
    badgeBg: '#dbeafe',
    badgeText: '#1e40af',
  },
  black: {
    accent: '#0f172a',
    border: '#cbd5e1',
    bg: '#f1f5f9',
    textOnAccent: '#ffffff',
    badgeBg: '#cbd5e1',
    badgeText: '#0f172a',
  },
  red: {
    accent: '#b91c1c',
    border: '#fee2e2',
    bg: '#fef2f2',
    textOnAccent: '#ffffff',
    badgeBg: '#fee2e2',
    badgeText: '#b91c1c',
  },
  green: {
    accent: '#047857',
    border: '#d1fae5',
    bg: '#ecfdf5',
    textOnAccent: '#ffffff',
    badgeBg: '#d1fae5',
    badgeText: '#047857',
  },
  purple: {
    accent: '#6b21a8',
    border: '#f3e8ff',
    bg: '#faf5ff',
    textOnAccent: '#ffffff',
    badgeBg: '#f3e8ff',
    badgeText: '#6b21a8',
  },
  orange: {
    accent: '#b45309',
    border: '#fde68a',
    bg: '#fffbeb',
    textOnAccent: '#ffffff',
    badgeBg: '#fde68a',
    badgeText: '#b45309',
  },
};

export function QuotationPrintView({ quotation, elementId = 'quotation-content', noShadow = false }: QuotationPrintViewProps) {
  // Resolve data fields
  const businessInfo = (quotation as any).from || (quotation as any)._businessInfo || {};
  const clientInfo   = quotation.billTo || (quotation as any).to || {};
  const items        = quotation.lineItems || (quotation as any).items || [];
  const taxData      = (quotation as any).tax || {};
  const discountData = (quotation as any).discount || {};
  const subtotal     = quotation.subtotal || (quotation as any).subtotal || 0;
  const headerTaxes  = quotation.settings?.headerTax?.taxes || (taxData && taxData.type !== 'none' ? [taxData] : []);

  const getTaxAmount = (t: any) => {
    if (t.type === 'percentage' || t.type === 'vat' || t.type === 'sst') {
      return (subtotal * (t.value || 0)) / 100;
    } else if (t.type === 'fixed') {
      return t.value || 0;
    }
    return 0;
  };

  // Determine if it is a Quotation or an Estimate
  const isEstimate = quotation.quotationNumber?.startsWith('EST');
  const documentTitle = isEstimate ? 'ESTIMATE' : 'QUOTATION';
  const documentNumberLabel = isEstimate ? 'Estimate #:' : 'Quotation #:';

  // Resolve template selection and color theme selection
  const templateId = ((quotation as any).templateId as TemplateId) || 'none';
  const themeId = (quotation as any).colorTheme;

  // Signature default color theme for each template style
  const tplColorDefaults: Record<string, string> = {
    ocean: 'blue',
    midnight: 'black',
    forest: 'slate',
    rose: 'green',
    sunset: 'orange',
    slate: 'slate',
    golden: 'orange',
    aurora: 'purple',
    carbon: 'black',
    clean: 'slate',
    none: 'slate',
  };

  // If user selected a colorTheme, use it; otherwise fallback to the template signature color
  const activeThemeId = themeId || tplColorDefaults[templateId] || 'slate';
  const colors = COLOR_THEMES[activeThemeId] || COLOR_THEMES.slate;

  // Map templateId names to the 5 core document layout formats
  let activeTemplateId: TemplateId = 'none';
  if (templateId === 'ocean' || templateId === 'aurora') {
    activeTemplateId = 'ocean'; // Corporate Accent layout
  } else if (templateId === 'midnight' || templateId === 'carbon') {
    activeTemplateId = 'midnight'; // Modern Executive layout
  } else if (templateId === 'forest' || templateId === 'slate') {
    activeTemplateId = 'forest'; // Left Sidebar layout
  } else if (templateId === 'rose' || templateId === 'sunset' || templateId === 'golden') {
    activeTemplateId = 'rose'; // Elegant Classic layout
  } else {
    activeTemplateId = 'none'; // Minimalist layout
  }

  // --- Shared Table Columns Headings ---
  const renderTableHead = (headerBg: string, headerColor: string, borderColor: string, customStyle?: React.CSSProperties) => (
    <thead>
      <tr style={{ background: headerBg, borderTop: `1px solid ${borderColor}`, borderBottom: `1px solid ${borderColor}`, ...customStyle }}>
        <th className="text-left p-3 font-semibold text-xs uppercase tracking-wider" style={{ color: headerColor }}>Description</th>
        <th className="text-center p-3 font-semibold text-xs uppercase tracking-wider w-20" style={{ color: headerColor }}>Qty</th>
        <th className="text-right p-3 font-semibold text-xs uppercase tracking-wider w-28" style={{ color: headerColor }}>Rate</th>
        <th className="text-right p-3 font-semibold text-xs uppercase tracking-wider w-28" style={{ color: headerColor }}>Amount</th>
      </tr>
    </thead>
  );

  // --- Shared Table Rows ---
  const renderTableRows = (bodyColor: string, labelColor: string, borderColor: string) => (
    <tbody>
      {items.map((item: any, index: number) => (
        <tr key={index} style={{ borderBottom: `1px solid ${borderColor}` }}>
          <td className="p-3 break-words text-left">
            <div className="break-words font-medium" style={{ color: bodyColor }}>{item.description}</div>
            {item.lineTax?.type && item.lineTax.type !== 'none' && (item.lineTax.value ?? 0) > 0 && (
              <div className="text-[11px] mt-1 font-medium" style={{ color: labelColor }}>
                {item.lineTax.label || 'Tax'}:{' '}
                {item.lineTax.type === 'percentage' || item.lineTax.type === 'vat' || item.lineTax.type === 'sst'
                  ? `${item.lineTax.value}%`
                  : formatCurrency(item.lineTax.value, quotation.currency)}
              </div>
            )}
            {item.lineDiscount?.type && item.lineDiscount.type !== 'none' && (item.lineDiscount.value ?? 0) > 0 && (
              <div className="text-[11px] font-medium" style={{ color: labelColor }}>
                Discount:{' '}
                {item.lineDiscount.type === 'percentage'
                  ? `${item.lineDiscount.value}%`
                  : formatCurrency(item.lineDiscount.value, quotation.currency)}
              </div>
            )}
          </td>
          <td className="text-center p-3" style={{ color: bodyColor }}>{item.quantity}</td>
          <td className="text-right p-3" style={{ color: bodyColor }}>
            {formatCurrency(item.unitPrice || item.rate || 0, quotation.currency)}
          </td>
          <td className="text-right p-3 font-semibold" style={{ color: bodyColor }}>
            {formatCurrency(item.subtotal || item.amount || 0, quotation.currency)}
          </td>
        </tr>
      ))}
    </tbody>
  );

  // --- Shared Totals Calculation Card ---
  const renderTotalsCard = (totalBg: string, labelColor: string, bodyColor: string, headingColor: string, showBorder = true) => (
    <div className="flex justify-end pt-4">
      <div data-page-break-avoid="true" className={`w-full sm:w-80 space-y-2 text-sm rounded-xl p-4 text-left ${showBorder ? 'border' : ''}`} style={{ background: totalBg, borderColor: colors.border }}>
        <div className="flex justify-between py-1 text-xs">
          <span style={{ color: labelColor }}>Subtotal:</span>
          <span className="font-semibold text-slate-800" style={{ color: bodyColor }}>
            {formatCurrency(quotation.subtotal || (quotation as any).subtotal || 0, quotation.currency)}
          </span>
        </div>

        {headerTaxes.map((t: any, index: number) => {
          if (t.type === 'none') return null;
          return (
            <div key={index} className="flex justify-between py-1 text-xs">
              <span style={{ color: labelColor }}>
                {t.label || 'Tax'} ({t.type === 'percentage' || t.type === 'vat' || t.type === 'sst'
                  ? `${t.value}%`
                  : formatCurrency(t.value, quotation.currency)}):
              </span>
              <span className="font-semibold text-slate-800" style={{ color: bodyColor }}>
                {formatCurrency(getTaxAmount(t), quotation.currency)}
              </span>
            </div>
          );
        })}

        {discountData.type && discountData.type !== 'none' && (
          <div className="flex justify-between py-1 text-xs">
            <span style={{ color: labelColor }}>
              Discount ({discountData.type === 'percentage'
                ? `${discountData.value}%`
                : formatCurrency(discountData.value, quotation.currency)}):
            </span>
            <span className="font-semibold text-red-650">
              -{formatCurrency((quotation as any).discountAmount || quotation.totalDiscount || 0, quotation.currency)}
            </span>
          </div>
        )}

        <div className="flex justify-between pt-3 font-bold text-base border-t-2" style={{ borderColor: headingColor, color: headingColor }}>
          <span>Total:</span>
          <span>
            {formatCurrency((quotation as any).total || quotation.totalAmount || 0, quotation.currency)}
          </span>
        </div>
      </div>
    </div>
  );

  return (
    <div
      id={elementId}
      className={`relative text-left bg-white ${noShadow ? 'shadow-none' : 'shadow-lg'}`}
      style={{ minHeight: '842px', width: '100%' }}
    >
      {activeTemplateId === 'ocean' ? (
        /* ================= LAYOUT 2: CORPORATE ACCENT ================= */
        <div className="p-6 sm:p-12 space-y-8 bg-white">
          {/* Header Layout */}
          <div className="flex flex-col sm:flex-row justify-between items-start gap-6 border-b-2 pb-6" style={{ borderBottomColor: colors.border }}>
            <div className="flex-1 min-w-0">
              <h1 className="text-4xl font-extrabold tracking-tight text-blue-800 uppercase mb-4" style={{ color: colors.accent }}>
                {documentTitle}
              </h1>
              <div className="space-y-1.5 text-xs">
                <div>
                  <span className="text-slate-500" style={{ color: colors.accent }}>{documentNumberLabel} </span>
                  <span className="font-bold text-slate-900">#{quotation.quotationNumber}</span>
                </div>
                <div>
                  <span className="text-slate-500" style={{ color: colors.accent }}>Issue Date: </span>
                  <span className="font-medium text-slate-800">
                    {quotation.issueDate ? format(quotation.issueDate, 'MMM dd, yyyy') : 'N/A'}
                  </span>
                </div>
                <div>
                  <span className="text-slate-500" style={{ color: colors.accent }}>Valid Until: </span>
                  <span className="font-medium text-slate-850">
                    {quotation.validUntil ? format(quotation.validUntil, 'MMM dd, yyyy') : 'N/A'}
                  </span>
                </div>
              </div>
            </div>

            <div className="text-left sm:text-right shrink-0">
              {businessInfo.logoUrl && (
                <div className="mb-4 h-14 flex items-start sm:justify-end justify-start">
                  <SafeLogoImage
                    logoUrl={businessInfo.logoUrl}
                    alt="Business Logo"
                    className="h-14 max-w-[180px] object-contain"
                  />
                </div>
              )}
              <h2 className="text-lg font-bold text-slate-850 break-words">
                {businessInfo.name || 'Business Name'}
              </h2>
              <p className="text-xs whitespace-pre-line mt-1 text-slate-500 break-words leading-relaxed">
                {businessInfo.address || 'Business Address'}
              </p>
              {businessInfo.email && (
                <p className="text-xs mt-1 text-slate-500 break-words">{businessInfo.email}</p>
              )}
              {businessInfo.phone && (
                <p className="text-xs text-slate-500 break-words">{businessInfo.phone}</p>
              )}
            </div>
          </div>

          {/* Bill To Grid */}
          <div className="pt-2">
            <div className="border-l-4 pl-4 py-1" style={{ borderColor: colors.accent }}>
              <div className="text-xs font-bold uppercase tracking-wider mb-2 text-slate-400">
                Quoted To
              </div>
              <p className="font-bold text-lg text-slate-800">
                {clientInfo.name || clientInfo.companyName || 'Client Name'}
              </p>
              <p className="text-xs whitespace-pre-line mt-1.5 text-slate-500 break-words leading-relaxed">
                {clientInfo.address || 'Client Address'}
              </p>
              {clientInfo.email && (
                <p className="text-xs mt-1 text-slate-550 break-words">{clientInfo.email}</p>
              )}
              {clientInfo.phone && (
                <p className="text-xs text-slate-555 break-words">{clientInfo.phone}</p>
              )}
            </div>
          </div>

          {/* Table */}
          <div className="overflow-x-auto w-full pt-4">
            <table className="w-full text-sm table-fixed min-w-[600px]">
              {renderTableHead(colors.bg, colors.accent, colors.border)}
              {renderTableRows('#0f172a', '#475569', colors.border)}
            </table>
          </div>

          {/* Summary / Totals */}
          {renderTotalsCard(colors.bg, '#475569', '#0f172a', colors.accent)}

          {/* Notes & Terms */}
          {(quotation.notes || quotation.terms) && (
            <div data-page-break-avoid="true" className="pt-6 space-y-4 text-xs border-t" style={{ borderColor: colors.border }}>
              {quotation.notes && (
                <div>
                  <div className="font-bold mb-1" style={{ color: colors.accent }}>Notes:</div>
                  <p className="whitespace-pre-line text-slate-600 break-words">{quotation.notes}</p>
                </div>
              )}
              {quotation.terms && (
                <div>
                  <div className="font-bold mb-1" style={{ color: colors.accent }}>Terms &amp; Conditions:</div>
                  <p className="whitespace-pre-line text-slate-600 break-words">{quotation.terms}</p>
                </div>
              )}
            </div>
          )}
        </div>
      ) : activeTemplateId === 'midnight' ? (
        /* ================= LAYOUT 3: MODERN EXECUTIVE ================= */
        <div className="bg-white flex flex-col min-h-full">
          {/* Header block with solid accent color strip */}
          <div className="text-white p-6 sm:p-10 flex flex-col sm:flex-row justify-between items-center gap-6" style={{ background: colors.accent }}>
            <div className="text-center sm:text-left">
              {businessInfo.logoUrl && (
                <div className="mb-3 h-12 flex items-center justify-center sm:justify-start">
                  <SafeLogoImage
                    logoUrl={businessInfo.logoUrl}
                    alt="Business Logo"
                    className="h-12 max-w-[160px] object-contain brightness-0 invert"
                  />
                </div>
              )}
              <h2 className="text-lg font-bold tracking-tight text-white">{businessInfo.name || 'Business Name'}</h2>
              <p className="text-[11px] text-slate-100 mt-1 whitespace-pre-line leading-normal">{businessInfo.address}</p>
              {businessInfo.email && <p className="text-[11px] text-slate-100">{businessInfo.email}</p>}
            </div>
            <div className="text-center sm:text-right shrink-0">
              <h1 className="text-3xl font-black tracking-widest text-white mb-2">{documentTitle}</h1>
              <div className="text-xs font-mono text-slate-350">{documentNumberLabel} {quotation.quotationNumber}</div>
            </div>
          </div>

          <div className="p-6 sm:p-10 space-y-8 flex-1">
            {/* Metadata & Billing Address */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 border-b pb-6 border-slate-200">
              <div className="space-y-1">
                <div className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-1.5 font-sans">Quoted To</div>
                <p className="font-bold text-base text-slate-800">
                  {clientInfo.name || clientInfo.companyName || 'Client Name'}
                </p>
                <p className="text-xs text-slate-500 whitespace-pre-line leading-relaxed">{clientInfo.address}</p>
                {clientInfo.email && <p className="text-xs text-slate-550 mt-1">{clientInfo.email}</p>}
                {clientInfo.phone && <p className="text-xs text-slate-555">{clientInfo.phone}</p>}
              </div>

              <div className="space-y-2 text-xs sm:text-right font-sans">
                <div className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-1.5">Details</div>
                <p className="text-slate-600"><span className="font-semibold text-slate-500">Issue Date:</span> {quotation.issueDate ? format(quotation.issueDate, 'MMM dd, yyyy') : 'N/A'}</p>
                <p className="text-slate-600"><span className="font-semibold text-slate-500">Valid Until:</span> {quotation.validUntil ? format(quotation.validUntil, 'MMM dd, yyyy') : 'N/A'}</p>
              </div>
            </div>

            {/* Table */}
            <div className="overflow-x-auto w-full">
              <table className="w-full text-sm table-fixed min-w-[600px]">
                {renderTableHead(colors.accent, colors.textOnAccent, colors.border)}
                {renderTableRows('#0f172a', '#475569', colors.border)}
              </table>
            </div>

            {/* Summary / Totals */}
            {renderTotalsCard(colors.bg, '#475569', '#0f172a', colors.accent)}

            {/* Notes & Terms */}
            {(quotation.notes || quotation.terms) && (
              <div data-page-break-avoid="true" className="pt-6 space-y-4 text-xs border-t border-slate-200">
                {quotation.notes && (
                  <div>
                    <div className="font-bold mb-1" style={{ color: colors.accent }}>Notes:</div>
                    <p className="whitespace-pre-line text-slate-550 break-words leading-relaxed">{quotation.notes}</p>
                  </div>
                )}
                {quotation.terms && (
                  <div>
                    <div className="font-bold mb-1" style={{ color: colors.accent }}>Terms &amp; Conditions:</div>
                    <p className="whitespace-pre-line text-slate-555 break-words leading-relaxed">{quotation.terms}</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      ) : activeTemplateId === 'forest' ? (
        /* ================= LAYOUT 4: LEFT SIDEBAR ================= */
        <div className="grid grid-cols-1 md:grid-cols-10 gap-8 p-6 sm:p-12 bg-white text-left">
          {/* Left Sidebar (3 cols) */}
          <div className="md:col-span-3 space-y-6 md:border-r md:pr-6 border-slate-200">
            {businessInfo.logoUrl && (
              <div className="h-14 flex items-center">
                <SafeLogoImage logoUrl={businessInfo.logoUrl} alt="Logo" className="h-14 max-w-[180px] object-contain" />
              </div>
            )}
            
            <div className="space-y-4 pt-4 border-t border-slate-100">
              <div>
                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest block">Reference</span>
                <span className="font-bold text-sm" style={{ color: colors.accent }}>#{quotation.quotationNumber}</span>
              </div>
              <div>
                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest block">Issue Date</span>
                <span className="text-xs font-semibold text-slate-700">
                  {quotation.issueDate ? format(quotation.issueDate, 'MMM dd, yyyy') : 'N/A'}
                </span>
              </div>
              <div>
                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest block">Valid Until</span>
                <span className="text-xs font-semibold text-slate-700">
                  {quotation.validUntil ? format(quotation.validUntil, 'MMM dd, yyyy') : 'N/A'}
                </span>
              </div>
            </div>

            {/* Compact Totals Block */}
            <div className="pt-6 border-t border-slate-200 space-y-2 text-xs">
              <div className="flex justify-between">
                <span className="text-slate-400">Subtotal:</span>
                <span className="font-semibold text-slate-700">{formatCurrency(quotation.subtotal, quotation.currency)}</span>
              </div>
              {headerTaxes.map((t: any, idx: number) => {
                if (t.type === 'none') return null;
                return (
                  <div key={idx} className="flex justify-between text-slate-550">
                    <span>{t.label || 'Tax'} ({t.value}%):</span>
                    <span>{formatCurrency(getTaxAmount(t), quotation.currency)}</span>
                  </div>
                );
              })}
              {discountData.type && discountData.type !== 'none' && (
                <div className="flex justify-between text-slate-555">
                  <span>Discount:</span>
                  <span className="text-red-650">-{formatCurrency((quotation as any).discountAmount || quotation.totalDiscount || 0, quotation.currency)}</span>
                </div>
              )}
              <div className="flex justify-between pt-2 border-t font-bold text-sm" style={{ borderTopColor: colors.accent, color: colors.accent }}>
                <span>Total:</span>
                <span>{formatCurrency(quotation.totalAmount, quotation.currency)}</span>
              </div>
            </div>
          </div>

          {/* Right Main Body (7 cols) */}
          <div className="md:col-span-7 space-y-8">
            <div className="flex justify-between items-start">
              <div>
                <h1 className="text-3xl font-extrabold tracking-tight uppercase" style={{ color: colors.accent }}>{documentTitle}</h1>
                <h2 className="text-lg font-bold text-slate-800 mt-2">{businessInfo.name || 'Business Name'}</h2>
                <p className="text-xs text-slate-500 whitespace-pre-line leading-relaxed">{businessInfo.address}</p>
                {businessInfo.email && <p className="text-xs text-slate-500">{businessInfo.email}</p>}
                {businessInfo.phone && <p className="text-xs text-slate-500">{businessInfo.phone}</p>}
              </div>
            </div>

            <div className="pt-2 border-t border-slate-100">
              <div className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1.5">Quoted To</div>
              <p className="font-bold text-sm text-slate-800">{clientInfo.name || 'Client Name'}</p>
              <p className="text-xs text-slate-550 whitespace-pre-line leading-relaxed">{clientInfo.address}</p>
              {clientInfo.email && <p className="text-xs text-slate-550">{clientInfo.email}</p>}
              {clientInfo.phone && <p className="text-xs text-slate-555">{clientInfo.phone}</p>}
            </div>

            {/* Table */}
            <div className="overflow-x-auto w-full">
              <table className="w-full text-xs table-fixed min-w-[500px]">
                {renderTableHead(colors.bg, colors.accent, colors.border)}
                {renderTableRows('#0f172a', '#475569', colors.border)}
              </table>
            </div>

            {/* Notes & Terms */}
            {(quotation.notes || quotation.terms) && (
              <div data-page-break-avoid="true" className="pt-6 space-y-4 text-xs border-t border-slate-200">
                {quotation.notes && (
                  <div>
                    <div className="font-bold mb-1" style={{ color: colors.accent }}>Notes:</div>
                    <p className="whitespace-pre-line text-slate-555 break-words leading-relaxed">{quotation.notes}</p>
                  </div>
                )}
                {quotation.terms && (
                  <div>
                    <div className="font-bold mb-1" style={{ color: colors.accent }}>Terms &amp; Conditions:</div>
                    <p className="whitespace-pre-line text-slate-555 break-words leading-relaxed">{quotation.terms}</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      ) : activeTemplateId === 'rose' ? (
        /* ================= LAYOUT 5: ELEGANT CLASSIC ================= */
        <div className="p-6 sm:p-12 space-y-8 bg-white font-serif text-slate-900">
          {/* Elegant Serif Header */}
          <div className="text-center space-y-4 border-b pb-6 border-slate-200">
            {businessInfo.logoUrl && (
              <div className="h-14 flex items-center justify-center">
                <SafeLogoImage logoUrl={businessInfo.logoUrl} alt="Logo" className="h-14 max-w-[180px] object-contain" />
              </div>
            )}
            <div>
              <h2 className="text-2xl font-bold tracking-tight" style={{ color: colors.accent }}>{businessInfo.name || 'Business Name'}</h2>
              <p className="text-xs text-slate-550 italic max-w-md mx-auto mt-1">{businessInfo.address}</p>
              {businessInfo.email && <p className="text-[11px] text-slate-555 font-sans">{businessInfo.email}</p>}
            </div>
            
            <h1 className="text-3xl font-semibold tracking-wider font-sans uppercase pt-4" style={{ color: colors.accent }}>{documentTitle}</h1>
          </div>

          {/* Details block */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 text-xs text-left">
            <div>
              <div className="text-[10px] font-bold text-slate-400 uppercase tracking-widest font-sans mb-1">Quoted To</div>
              <p className="font-bold text-sm text-slate-800">{clientInfo.name || 'Client Name'}</p>
              <p className="text-xs text-slate-500 mt-1 leading-relaxed whitespace-pre-line">{clientInfo.address}</p>
            </div>
            
            <div className="space-y-1.5 text-xs sm:text-right font-sans">
              <div className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">Document Index</div>
              <p className="text-slate-650"><span className="font-semibold text-slate-500">Quotation #:</span> {quotation.quotationNumber}</p>
              <p className="text-slate-655"><span className="font-semibold text-slate-500">Issue Date:</span> {quotation.issueDate ? format(quotation.issueDate, 'MMM dd, yyyy') : 'N/A'}</p>
              <p className="text-slate-655"><span className="font-semibold text-slate-500">Valid Until:</span> {quotation.validUntil ? format(quotation.validUntil, 'MMM dd, yyyy') : 'N/A'}</p>
            </div>
          </div>

          {/* Table with double-line borders */}
          <div className="overflow-x-auto w-full pt-4">
            <table className="w-full text-sm table-fixed min-w-[600px]">
              {renderTableHead('transparent', colors.accent, colors.border, { borderTop: `2px double ${colors.border}`, borderBottom: `2px double ${colors.border}` })}
              {renderTableRows('#0f172a', '#475569', colors.border)}
            </table>
          </div>

          {/* Summary */}
          {renderTotalsCard('transparent', '#475569', '#0f172a', colors.accent, false)}

          {/* Notes & Terms */}
          {(quotation.notes || quotation.terms) && (
            <div data-page-break-avoid="true" className="pt-6 space-y-4 text-xs border-t text-center italic text-slate-555 border-double border-t-4" style={{ borderColor: colors.border }}>
              {quotation.notes && (
                <div>
                  <div className="font-bold font-sans not-italic text-xs uppercase tracking-wider mb-1" style={{ color: colors.accent }}>Notes</div>
                  <p className="whitespace-pre-line max-w-xl mx-auto break-words leading-relaxed">{quotation.notes}</p>
                </div>
              )}
              {quotation.terms && (
                <div>
                  <div className="font-bold font-sans not-italic text-xs uppercase tracking-wider mb-1" style={{ color: colors.accent }}>Terms &amp; Conditions</div>
                  <p className="whitespace-pre-line max-w-xl mx-auto break-words leading-relaxed">{quotation.terms}</p>
                </div>
              )}
            </div>
          )}
        </div>
      ) : (
        /* ================= LAYOUT 1: MINIMALIST (DEFAULT) ================= */
        <div className="p-6 sm:p-12 space-y-8 bg-white">
          {/* Header Layout */}
          <div className="flex flex-col sm:flex-row justify-between items-start gap-6">
            <div className="flex-1 min-w-0">
              {businessInfo.logoUrl && (
                <div className="mb-4 h-14 flex items-start justify-start">
                  <SafeLogoImage
                    logoUrl={businessInfo.logoUrl}
                    alt="Business Logo"
                    className="h-14 max-w-[180px] object-contain"
                  />
                </div>
              )}
              <h2 className="text-xl font-bold text-slate-855 break-words">
                {businessInfo.name || 'Business Name'}
              </h2>
              <p className="text-xs whitespace-pre-line mt-1 text-slate-500 break-words leading-relaxed">
                {businessInfo.address || 'Business Address'}
              </p>
              {businessInfo.email && (
                <p className="text-xs mt-1 text-slate-555 break-words">{businessInfo.email}</p>
              )}
              {businessInfo.phone && (
                <p className="text-xs text-slate-555 break-words">{businessInfo.phone}</p>
              )}
            </div>

            <div className="text-left sm:text-right shrink-0">
              <h1 className="text-4xl font-extrabold tracking-widest mb-3" style={{ color: colors.accent }}>
                {documentTitle}
              </h1>
              <div className="space-y-1 text-xs">
                <div>
                  <span style={{ color: '#64748b' }}>{documentNumberLabel} </span>
                  <span className="font-semibold text-slate-800">#{quotation.quotationNumber}</span>
                </div>
                <div>
                  <span style={{ color: '#64748b' }}>Issue Date: </span>
                  <span className="font-medium text-slate-800">
                    {quotation.issueDate ? format(quotation.issueDate, 'MMM dd, yyyy') : 'N/A'}
                  </span>
                </div>
                <div>
                  <span style={{ color: '#64748b' }}>Valid Until: </span>
                  <span className="font-medium text-slate-855">
                    {quotation.validUntil ? format(quotation.validUntil, 'MMM dd, yyyy') : 'N/A'}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Bill To Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 pt-2">
            <div className="p-4 rounded-xl border text-left" style={{ background: colors.bg, borderColor: colors.border }}>
              <div className="text-xs font-bold uppercase tracking-wider mb-2 text-slate-400" style={{ color: colors.accent }}>
                Quoted To:
              </div>
              <p className="font-bold text-base text-slate-800">
                {clientInfo.name || clientInfo.companyName || 'Client Name'}
              </p>
              <p className="text-xs whitespace-pre-line mt-1 text-slate-500 break-words leading-relaxed">
                {clientInfo.address || 'Client Address'}
              </p>
              {clientInfo.email && (
                <p className="text-xs mt-1 text-slate-555 break-words">{clientInfo.email}</p>
              )}
              {clientInfo.phone && (
                <p className="text-xs text-slate-555 break-words">{clientInfo.phone}</p>
              )}
            </div>
          </div>

          {/* Table */}
          <div className="overflow-x-auto w-full pt-2">
            <table className="w-full text-sm table-fixed min-w-[600px]">
              {renderTableHead(colors.bg, colors.accent, colors.border)}
              {renderTableRows('#0f172a', '#475569', colors.border)}
            </table>
          </div>

          {/* Summary / Totals */}
          {renderTotalsCard(colors.bg, '#475569', '#0f172a', colors.accent)}

          {/* Notes & Terms */}
          {(quotation.notes || quotation.terms) && (
            <div data-page-break-avoid="true" className="pt-6 space-y-4 text-xs border-t" style={{ borderColor: colors.border }}>
              {quotation.notes && (
                <div>
                  <div className="font-bold mb-1" style={{ color: colors.accent }}>Notes:</div>
                  <p className="whitespace-pre-line text-slate-600 break-words">{quotation.notes}</p>
                </div>
              )}
              {quotation.terms && (
                <div>
                  <div className="font-bold mb-1" style={{ color: colors.accent }}>Terms &amp; Conditions:</div>
                  <p className="whitespace-pre-line text-slate-600 break-words">{quotation.terms}</p>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
